import { FormattedMessage, useIntl } from 'react-intl';
import { ResponsiveLine } from '@nivo/line';
import { Button, Form, Modal, Skeleton, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';
import { ContentWrapper, EllipsisButton, Group } from '@/components/_common';
import AverageTicketChartModal from './AverageTicketChartModal';
import { useChartAverageTicket } from '@/core/domain/workorder/ManagementCharts/ManagementChart.hooks';
import {
  AverageTicketFormValues,
  AverageTicketParams,
} from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';
import {
  useChartTheme,
  useConvertChartData,
  useDisclosure,
  useWindowSize,
} from '@/hooks';
import moment from 'moment';
import { DateTimeFormat } from '@/utils/moment';
import { WorkOrderComplementType } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import RedirectIcon from '@/components/_common/RedirectIcon';
import { buildParamsStr } from '@/utils/queryParams';
import { ManagerialIncomeRequestType } from '@/core/domain/NetReport/Managerial/Managerial.types';

const { Title, Paragraph } = Typography;

function AverageTicketChart() {
  const intl = useIntl();
  const { isMobile } = useWindowSize();
  const [form] = Form.useForm<AverageTicketFormValues>();
  const [modalVisible, modalVisibleAction] = useDisclosure();
  const { data, mutateAsync, isLoading } = useChartAverageTicket();
  const chartTheme = useChartTheme();

  const [filter, setFilter] = useState<AverageTicketParams>({
    startDate: moment().subtract(5, 'months').format(DateTimeFormat),
    endDate: moment().format(DateTimeFormat),
    types: WorkOrderComplementType.WorkOrder,
  });

  const chartData = useConvertChartData(data?.chartData);

  useEffect(() => {
    mutateAsync(filter);
  }, []);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const obj: AverageTicketParams = {
        companiesIds: values.companiesIds,
        startDate: values.dates[0].format('YYYY-MM-DD'),
        endDate: values.dates[1].format('YYYY-MM-DD'),
        types: values.types,
      };

      setFilter(obj);

      await mutateAsync(obj);

      modalVisibleAction.close();
    } catch {}
  };

  const handleModalReset = async () => {
    const startDate = moment().subtract(5, 'months');
    const endDate = moment();
    const obj = {
      startDate: startDate.format(DateTimeFormat),
      endDate: endDate.format(DateTimeFormat),
      types: WorkOrderComplementType.WorkOrder,
    };

    setFilter(obj);

    await mutateAsync(obj);

    modalVisibleAction.close();
    form.resetFields();
  };

  const paramsRedirectReport = {
    initialDate: filter.startDate,
    finalDate: filter.endDate,
    companiesIds: filter.companiesIds,
    requestType: ManagerialIncomeRequestType.averageTicketPartsServices,
  };

  return (
    <>
      <ContentWrapper
        style={{
          height: '360px',
          padding: '24px',
          overflow: 'hidden',
        }}
        extra={
          <Group style={{ flexDirection: isMobile ? 'column' : undefined }}>
            <EllipsisButton onClick={modalVisibleAction.open} />

            <RedirectIcon
              placement="left"
              paths={[
                {
                  title: 'Relatório de Ticket Médio',
                  url: `/app/reports/managerial/invoicing?${buildParamsStr(
                    paramsRedirectReport,
                  )}`,
                },
              ]}
            />
          </Group>
        }
      >
        <Skeleton loading={isLoading}>
          <Title level={5} style={{ textAlign: 'center', marginBottom: '4px' }}>
            <FormattedMessage id="dashboard.averageTicketReport" />
          </Title>
          <Paragraph style={{ textAlign: 'center', margin: '4px 0 4px 0' }}>
            <FormattedMessage id="dashboard.lastSixMonths" />
          </Paragraph>

          <ResponsiveLine
            data={chartData ? chartData : []}
            enableSlices="x"
            margin={{ top: 15, right: 25, bottom: 90, left: 70 }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: 0,
              max: 'auto',
              reverse: false,
            }}
            yFormat={(value) => `${currencyFormatter(value)}`}
            curve="monotoneX"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legendOffset: 36,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              tickValues: 5,
              legend: intl.formatMessage({
                id: 'dashboard.averageTicketReport.averageTicketPartsAndServices',
              }),
              format: (value) => `${metricPrefixFormatter(+value)}`,
              legendOffset: -50,
              legendPosition: 'middle',
            }}
            enableGridX={false}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            useMesh
            theme={chartTheme}
          />
        </Skeleton>
      </ContentWrapper>

      <Modal
        title="Filtros"
        open={modalVisible}
        onCancel={modalVisibleAction.close}
        width={400}
        footer={[
          <Button type="primary" ghost onClick={handleModalReset}>
            Resetar
          </Button>,
          <Button type="primary" onClick={handleOk} loading={isLoading}>
            Atualizar
          </Button>,
        ]}
      >
        <AverageTicketChartModal form={form} />
      </Modal>
    </>
  );
}

export default AverageTicketChart;
